import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, effect, forwardRef, Input, model, OnDestroy, output, signal, untracked, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslateModule } from '@ngx-translate/core';
import { pickPreferredProductPriceInfo, xDivide, xMultiply } from '@phoenix/common';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { BehaviorSubject } from 'rxjs';
import { IPHXEditComponent } from '../../../../../@core/interfaces/IPHXEditComponent.interface';
import { DecimalPlacesCustomPipe } from '../../../../../@core/pipes';
import { I18nService } from '../../../../../@core/services/i18n/i18n.service';
import { Document, DocumentLineItem, PriceSourceType, ProductPriceInfo, ProductPriceInfoInput, TaxType } from '../../../../../generated/generated-types';
import { PhxInputNumberComponent } from '../../../../../shared/components/phx-input-number/phx-input-number.component';
import { PhxSelect } from '../../../../../shared/components/phx-select/phx-select.component';
import { ProductPriceInfoService } from '../../../services/product-price-info.service';
// var equal = require('fast-deep-equal');
/**
 * @description Load existing prices of priceList and customerPriceList that is in document
 * @param doc Document
 */

export type ProductPriceInfoExt = ProductPriceInfo & {
  originPrice?: number | null;
  unitConversionPrice?: number | null;
}

export type MenuItemPriceInfoExt = MenuItem & {
  priceUnit: string;
  pricePerConversion: string;
  priceList: string;
  minimumQuantity: string;
  minimumQuantityPerConversion: string;
}

@Component({
  selector: 'app-product-price-info',
  templateUrl: './product-price-info.component.html',
  providers: [
    ProductPriceInfoService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ProductPriceInfoComponent),
      multi: true
    }
  ],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    PhxInputNumberComponent,
    ButtonModule,
    MenuModule,
    TranslateModule
  ]
})
export class ProductPriceInfoComponent implements IPHXEditComponent, OnDestroy, ControlValueAccessor {

  // priceIncludesTax - when setting has always to rely on this.doc?.taxType because this is what is calculated and set as price - and so value will
  // be wrong if we take item.priceIncludesTax from the item

  // Add these properties for ControlValueAccessor
  private onChange: (value: number | ProductPriceInfoExt | null) => void = () => { };
  private onTouched: () => void = () => { };
  disabled = model(false);

  upDownButtons = model<boolean>();

  deletePriceInfoOnDestroy = model<boolean>(true);

  ngModel = model<number>()
  private readonly ngModelSignal = this.ngModel;

  // Implement ControlValueAccessor methods
  writeValue(value: number | null): void {
    if (value !== this.ngModelSignal()) {
      this.ngModelSignal.set(value);
      this.ngModelWithDecimalPlaces.set(this.decimalPipe.transform(value, this.lineItem()?.decimalPlaces));
      this.cdRef.markForCheck();
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    this.cdRef.markForCheck();
  }

  doc = model<Document>(null);

  productPriceInfos = model<ProductPriceInfoExt[]>([] as ProductPriceInfoExt[]); // is combined with bulkPriceInfos

  decimalPlacesInputSettings = model<number>(null);

  lineItem = model<DocumentLineItem>(null);

  currentProductPriceFilter: ProductPriceInfoInput;

  loadingSig = model<boolean>(false);

  quantity = model<number>();

  _internalAutoSelect = model<boolean>(true);

  initializationDone = model<boolean>(false);
  ngModelWithDecimalPlaces = signal<string>(null);

  get isManualPrice(): boolean {
    return this.lineItem()?.priceSource === PriceSourceType.MANUALPRICE && this.lineItem()?.itemPrice !== null && this.lineItem()?.itemPrice !== undefined;
  }

  get isCustomerPrice(): boolean {
    return this.lineItem()?.priceSource === PriceSourceType.CUSTOMERPRICE
  }

  @Input()
  matchingQuantity: boolean = false;

  @Input()
  justEditMode: boolean = false;

  selectedPriceValue = model<number>(null);

  @ViewChild('ppiSelect')
  ppiSelect: PhxSelect<ProductPriceInfoExt>;

  isDestroyed$ = new BehaviorSubject<boolean>(false);

  buttonMenu: MenuItem[] = [];

  selectedPriceInfoChange = output<ProductPriceInfoExt>();
  PriceSourceType = PriceSourceType;

  constructor(
    private service: ProductPriceInfoService,
    private i18n: I18nService,
    public cdRef: ChangeDetectorRef,
    private decimalPipe: DecimalPlacesCustomPipe
  ) {

    effect(() => {
      const currentNgModel = this.ngModelSignal();
      untracked(() => {
        if (typeof currentNgModel === 'object') {
          //still can be null
          this.selectedPriceValue.set((currentNgModel as ProductPriceInfoExt)?.price);
        }
        else {
          this.selectedPriceValue.set(+currentNgModel);
        }
      });

      // This section is a replacement for set ngModel(value: number | null), since it was used to set something, 
      // it means a change into ngModel, which for me is basically same thing as detect the change and call onChange & onTouched as it was done before.
      // We might dont need to do it anymore, however we need to check.
      this.onChange(currentNgModel);
      this.onTouched();
    });

    /**
     * @description This effect syncs ngModelWithDecimalPlaces when ngModel or lineItem decimalPlaces changes
     */
    effect(() => {
      const currentNgModel = this.ngModelSignal();
      const decimalPlaces = this.lineItem()?.decimalPlaces;
      untracked(() => {
        this.ngModelWithDecimalPlaces.set(this.decimalPipe.transform(currentNgModel, decimalPlaces));
      });
    });

    /**
     * @description This effect just creates the menuItems for productPriceInfos
     */
    effect(() => {
      if (this.productPriceInfos()?.length > 0) {
        const menuItemsRefetched: MenuItemPriceInfoExt[] = [];
        for (let i = 0; i < this.productPriceInfos()?.length; i++) {
          const priceInfo = this.productPriceInfos()[i];

          let formattedPricePerConversion: string;
          let minimumQuantity: string;
          let minimumQuantityPerConversion: string;

          if (this.lineItem()?.quantityToBaseUnitFactor && this.lineItem()?.quantityToBaseUnitFactor !== 1)
            formattedPricePerConversion = this.lineItem()?.quantityToBaseUnitFactor ? this.decimalPipe.transform(xMultiply(priceInfo.price, this.lineItem()?.quantityToBaseUnitFactor ?? 1), this.lineItem()?.decimalPlaces) : null;
          const formattedPrice = this.decimalPipe.transform(priceInfo.price, this.lineItem()?.decimalPlaces);
          if (priceInfo.quantity > 0)
            minimumQuantity = this.decimalPipe.transform(priceInfo.quantity, this.lineItem()?.decimalPlaces);
          if (priceInfo.quantity > 0 && this.lineItem()?.quantityToBaseUnitFactor && this.lineItem()?.quantityToBaseUnitFactor !== 1)
            minimumQuantityPerConversion = this.decimalPipe.transform(xDivide(this.lineItem()?.quantityToBaseUnitFactor ? priceInfo.quantity : priceInfo.quantity, (this.lineItem()?.quantityToBaseUnitFactor ?? 1)), this.lineItem()?.decimalPlaces);
          menuItemsRefetched.push({
            priceUnit: formattedPrice,
            pricePerConversion: formattedPricePerConversion,
            priceList: priceInfo.sourceIdentifier,
            minimumQuantity: minimumQuantity,
            minimumQuantityPerConversion: minimumQuantityPerConversion,
            icon: "fa-solid fa-circle",
            iconStyle: { color: this.productPriceInfos()[i].color },
            value: this.productPriceInfos()[i].price,
            command: () => {
              this.setSelectedPriceAndEmitChange(this.productPriceInfos()[i]);
            }
          });
        }
        this.buttonMenu = menuItemsRefetched;
        untracked(() => {
          if (!this.lineItem()?.id)
            this.preSelectByCustomerOrListPrice();
        });
      }
    });
  }

  onPriceInputChange(value: number | null): void {
    if (value === null) {
      value = 0;
      this.ngModelSignal.set(0);
    }
    if (!Number.isFinite(value)) {
      return;
    }
    this.autoSelectPriceSource(value, this.lineItem()?.quantity, true);
  }

  /**
   * @description Just the emit of ProductPriceInfo or ngModel as ProductPriceInfo
   * @param e? ProductPriceInfo
   */
  emitProductPriceInfoChange(e?: ProductPriceInfoExt | null) {

    if (e)
      this.selectedPriceInfoChange.emit(e);
    else {
      this.selectedPriceInfoChange.emit({
        price: this.ngModelSignal(),
        sourceType: PriceSourceType.MANUALPRICE,
      });
    }
  }

  /**
   * @description Set the selectedPrice and the formControl value
   * @param e ProductPriceInfo
   */
  setSelectedPriceAndEmitChange(e: ProductPriceInfoExt | null) {
    if (this.isDestroyed$.value) {
      console.warn("ProductPriceInfoComponent is destroyed, so we dont set the selectedPrice");
      return;
    }
    const priceToSet = e?.unitConversionPrice ?? e?.price;
    this.ngModelSignal.set(priceToSet);
    this.emitProductPriceInfoChange(e);
  }


  /**
   * @description sets the new price as manual price (productPriceInfo) 
   * @important should be used only if no other price exists (customerPrice, listPrice, bulkPrice)
   * @param value number
   * @returns ProductPriceInfoExt
   */
  setManualPrice(value: number) {

    if (!this.doc() || value === undefined)
      return;

    if (value === null)
      value = 0;

    let newValue: ProductPriceInfoExt = {
      originPrice: +value,
      price: +value,
      currency: this.doc().currency,
      priceIncludesTax: this.doc().taxType === TaxType.GROSS || this.doc().taxType === TaxType.FREE,
      sourceType: PriceSourceType.MANUALPRICE,
      sourceIdentifier: this.i18n.translate(_("enums." + PriceSourceType.MANUALPRICE)),
      nonCashDiscountable: null,
      nonDiscountable: null,
      nonProvisionable: null,
      priority: 4,
      color: "black"
    }

    this.setSelectedPriceAndEmitChange(newValue);

    return newValue;
  }

  /**
   * @description Get applicable product price infos, filtered by customer price if needed
   * @returns ProductPriceInfoExt[]
   */
  private getApplicableProductPriceInfos(): ProductPriceInfoExt[] {
    let applicableProductPriceInfos = this.productPriceInfos();
    if (this.isCustomerPrice) {
      applicableProductPriceInfos = applicableProductPriceInfos.filter(x => x.sourceType == PriceSourceType.CUSTOMERPRICE);
    }
    return applicableProductPriceInfos;
  }

  /**
   * @description Calculate checked quantity accounting for unit conversion rate
   * @param quantity 
   * @returns number
   */
  private getCheckedQuantity(quantity: number): number {
    return xMultiply(quantity, this.lineItem()?.quantityToBaseUnitFactor ?? 1);
  }

  /**
   * @description Get minimum quantities from product price infos
   * @param infos 
   * @returns number[]
   */
  private getMinQuantities(infos: ProductPriceInfoExt[]): number[] {
    return infos
      .map(x => x.quantity)
      .filter((x): x is number => x != null);
  }

  /**
   * @description Find the best bulk price (highest quantity that applies) from given infos
   * @param infos 
   * @param checkedQuantity 
   * @returns ProductPriceInfoExt | null
   */
  private findBestBulkPrice(infos: ProductPriceInfoExt[], checkedQuantity: number): ProductPriceInfoExt | null {
    const minQuantities = this.getMinQuantities(infos);
    const lowestQuantityForBulkPrice = minQuantities.length > 0 ? Math.min(...minQuantities) : null;

    if (lowestQuantityForBulkPrice && checkedQuantity >= lowestQuantityForBulkPrice) {
      const validBulkPriceInfos = infos.filter(x => x.quantity != null && x.quantity <= checkedQuantity);
      if (validBulkPriceInfos.length > 0) {
        return validBulkPriceInfos.sort((a, b) => b.quantity - a.quantity)[0];
      }
    }
    return null;
  }

  /**
   * Auto-pick by shared priority: CUSTOMERPRICE → ACCESSORYCONFIG → PRICELIST.
   * @returns boolean - if something is selected
   */
  preSelectByCustomerOrListPrice(): boolean {
    if (this.isManualPrice)
      return false;

    const preferred = pickPreferredProductPriceInfo(this.productPriceInfos());
    if (preferred) {
      this.setSelectedPriceAndEmitChange(preferred);
      return true;
    }
    return false;
  }

  /**
   * @description This method sets the price per quantity change from bulkPrices or listPrices if no bulkPrices are available
   * @param quantity 
   * @returns boolean - if something is selected
   */
  preSelectByQuantity(quantity: number): boolean {
    if (this.isManualPrice)
      return false;

    const applicableProductPriceInfos = this.getApplicableProductPriceInfos();
    const checkedQuantity = this.getCheckedQuantity(quantity);
    const bestBulkPrice = this.findBestBulkPrice(applicableProductPriceInfos, checkedQuantity);

    if (bestBulkPrice) {
      if (this.selectedPriceValue() != bestBulkPrice.price) { //only set if changed
        this.setSelectedPriceAndEmitChange(bestBulkPrice);
        return true;
      }
    } else {
      // No bulk price applies, try customer/list price fallback
      return this.preSelectByCustomerOrListPrice();
    }
    return false;
  }

  /**
   * @description This method gets the priceSource (productPriceInfo) according to quantity and price and sets it
   * @important no price amount manipulation!
   * @param price 
   * @param quantity 
   * @param isManual - if true, always sets as manual price
   * @returns boolean - true if a price source was selected, false if manual price was set
   */
  autoSelectPriceSource(price: number, quantity: number, isManual: boolean = false): boolean {
    if (this.isManualPrice || isManual) {
      this.setManualPrice(price);
      return false;
    }

    if (price === null || price === undefined) {
      this.ngModelSignal.set(null);
      return false;
    }

    this.productPriceInfos.set([...this.productPriceInfos()]);

    const applicableProductPriceInfos = this.getApplicableProductPriceInfos();
    const checkedQuantity = this.getCheckedQuantity(quantity);

    // First, try to find exact match: same quantity AND same price (considering unit conversion price)
    for (const x of applicableProductPriceInfos) {
      const xQuantity = x.quantity;
      const xPrice = x.unitConversionPrice ?? x.price;

      // Match quantity exactly (accounting for unit conversion) and price exactly
      // Only match when quantity is not null (exact quantity match required)
      if (xQuantity != null && xQuantity === checkedQuantity && xPrice === price) {
        this.setSelectedPriceAndEmitChange(x);
        return true;
      }
    }

    // Second, try per quantity
    const priceMatchingInfos = applicableProductPriceInfos.filter(x => {
      const xQuantity = x.quantity;
      if (xQuantity != null && xQuantity <= checkedQuantity) {
        return true;
      }
      return false;
    });

    if (priceMatchingInfos.length > 0) {
      const bestBulkPrice = this.findBestBulkPrice(priceMatchingInfos, checkedQuantity);
      if (bestBulkPrice) {
        this.setSelectedPriceAndEmitChange(bestBulkPrice);
        return true;
      }

      // Quantity doesn't meet bulk price requirements, but price matches - use non-bulk price
      const nonBulkPriceMatch = priceMatchingInfos.find(x => !x.quantity || x.quantity === null);
      if (nonBulkPriceMatch) {
        this.setSelectedPriceAndEmitChange(nonBulkPriceMatch);
        return true;
      }
    }

    // Third, if no price match found, try to find best bulk price by quantity only
    const bestBulkPrice = this.findBestBulkPrice(applicableProductPriceInfos, checkedQuantity);
    if (bestBulkPrice) {
      this.setSelectedPriceAndEmitChange(bestBulkPrice);
      return true;
    }

    // No bulk price applies, try customer/list price fallback
    const fallbackMatch = this.preSelectByCustomerOrListPrice();
    if (fallbackMatch) {
      return true;
    }

    // Finally, if nothing matches, set as manual price
    if (price) {
      this.setManualPrice(price);
      return false;
    }

    return false;
  }

  /**
   * @description Clear the productPriceInfos array, the selectedPrice, and the currentProductPriceFilter to null
   * @returns void
   */
  clear(): void {
    this.ngModelSignal.set(null);
    this.setSelectedPriceAndEmitChange(null);
  }

  /**
   * @important isDestroyed$ is important in widget-add-edit-line-item-list - for the fetch/refetch of the productPriceInfos
   */
  ngOnDestroy(): void {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
    if (this.deletePriceInfoOnDestroy())
      this.productPriceInfos.set([]);
    this.selectedPriceInfoChange.emit(null);
  }

}
